"use client";

import { useId, useState } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon, FilterSettingsIcon } from "@/components/appIcons";
import { FacetFilterControls } from "@/components/facets/facet-filter-controls";
import { FacetMobileSort } from "@/components/facets/facet-mobile-sort";
import { Overlay } from "@/components/ui/_internal/overlay";
import { Button } from "@/components/ui/button";
import type { FilterOption } from "@/components/ui/filter-select";
import { FilterToolbarControls } from "@/components/ui/filter-toolbar";
import { activeFacetFilterCount } from "@/facets/active-facet-filter-count";
import type { SurfaceConfig } from "@/facets/config";
import { useFacetFilters, type FacetFiltersController } from "@/facets/use-facet-filters";
import { cn } from "@/lib/utils";
import type { FacetedSearchState } from "@garimpa/lib/searchState";

interface FacetFiltersModalProps {
  surface: SurfaceConfig;
  options: Record<string, FilterOption[]>;
  controller: FacetFiltersController;
  sortOptions: FilterOption[];
  className?: string;
}

/**
 * Mobile "Filters" button that opens a bottom sheet with sort and the secondary
 * facets. Edits stay on a draft copy of the search state until "Apply", so the
 * results reload once; closing the sheet discards them.
 */
export function FacetFiltersModal({
  surface,
  options,
  controller,
  sortOptions,
  className,
}: FacetFiltersModalProps) {
  const t = useTranslations("facets");
  const ta = useTranslations("globals.accessibility");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<FacetedSearchState | undefined>(controller.state);
  const draftController = useFacetFilters({ state: draft, onChange: setDraft, surface });
  const titleId = useId();
  const activeCount = activeFacetFilterCount(surface, controller);

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t("filtersButton")}
        onClick={() => {
          setDraft(controller.state);
          setOpen(true);
        }}
        className={cn(
          "inline-flex h-11 shrink-0 items-center gap-2 rounded-md border border-border bg-secondary/50 px-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary",
          className,
        )}
      >
        <FilterSettingsIcon className="text-[18px]" />
        {activeCount > 0 && (
          <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold text-primary-foreground tabular-nums">
            {activeCount}
          </span>
        )}
      </button>
      <Overlay
        open={open}
        onOpenChange={setOpen}
        variant="sheet"
        aria-labelledby={titleId}
        className="flex max-h-[92dvh] flex-col overflow-hidden"
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 pb-2">
          <h2 id={titleId} className="text-base font-semibold">
            {t("filterBy")}
          </h2>
          <button
            type="button"
            aria-label={ta("close")}
            onClick={() => setOpen(false)}
            className="relative after:absolute after:-inset-0.5 after:content-[''] flex size-10 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <CloseIcon className="text-[18px]" />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-4 py-4">
          <FacetMobileSort
            controller={draftController}
            options={sortOptions}
            placeholder={t("sortBy")}
          />
          <FilterToolbarControls label={t("filterBy")}>
            <FacetFilterControls
              surface={surface}
              options={options}
              controller={draftController}
              showClear={false}
            />
          </FilterToolbarControls>
        </div>
        <div className="flex shrink-0 gap-3 border-t border-border px-4 py-3">
          <Button
            variant="outline"
            className="h-11 flex-1"
            onClick={draftController.clearAll}
            disabled={!draftController.hasActive}
          >
            {t("clear")}
          </Button>
          <Button
            className="h-11 flex-[2] font-semibold"
            onClick={() => {
              controller.applyDraft(draft);
              setOpen(false);
            }}
          >
            {t("applyFilters")}
          </Button>
        </div>
      </Overlay>
    </>
  );
}
